import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { PAConstants, WMICDriverEndorsementUtil, WMICRichTextUtil } from 'wmic-pe-portals-utils-js';
import { WMICDriverEndorsementsDetailsComponent } from 'wmic-pe-capability-gateway-common-pa-react';
import { Loader } from '@jutro/components';
import { CheckboxField } from '@jutro/legacy/components';
import messages from './WMICDriverEndorsementsCheckbox.messages';
import styles from './WMICDriverEndorsementsCheckbox.module.scss';

function WMICDriverEndorsementsCheckbox(props) {
    const {
        id,
        driverEndorsement,
        driver,
        selectedVehicleId,
        baseState,
        rateAsOfDate,
        paLobDataVM,
        handleChange,
        readOnly,
        hideUnchecked,
    } = props;
    const translator = useContext(TranslatorContext);
    const [isLoading, setIsLoading] = useState(false);
    const [checked, setChecked] = useState();

    const driverEndorsementPublicID = driverEndorsement?.publicID;

    const getEndorsementLabel = covId => {
        if (
            [
                PAConstants.accidentWaiverCovPublicId,
                PAConstants.accidentWaiverMtcCovPublicId,
                PAConstants.reducAmtForNamedCov_28_PublicId,
                PAConstants.minorConvictionWaiver39bPublicId,
                PAConstants.reducAmtForNamedCov_28A_PublicId,
            ].includes(covId)
        ) {
            return driver.displayName;
        }

        let label;
        switch (covId) {
            case PAConstants.article6ExcludedDriver_WMIC_PublicId:
                label = messages.article6Excluded;
                break;
            default:
                return null;
        }

        return WMICRichTextUtil.translateRichText(translator(label, { name: driver.displayName }));
    };

    const getIsChecked = () => {
        if (
            driverEndorsementPublicID === PAConstants.accidentWaiverCovPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }
        if (
            driverEndorsementPublicID === PAConstants.accidentWaiverMtcCovPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverMtcEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }
        if (
            driverEndorsementPublicID === PAConstants.minorConvictionWaiver39bPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.minorConvictionWaiverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }
        if (
            driverEndorsementPublicID === PAConstants.reducAmtForNamedCov_28A_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.excludedDriverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }
        if (
            driverEndorsementPublicID === PAConstants.reducAmtForNamedCov_28_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.reductionDriverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }
        if (
            driverEndorsementPublicID === PAConstants.article6ExcludedDriver_WMIC_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverMtcEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
                vehiclePublicID: selectedVehicleId,
            })
        ) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        setChecked(getIsChecked());
    }, [selectedVehicleId]);

    return (
        <React.Fragment>
            {isLoading ? (
                <Loader />
            ) : hideUnchecked && !checked ? null : (
                <div
                    className={cx('gw-pb-4', {
                        [styles.checkboxDetails]: WMICDriverEndorsementUtil.shouldShowCheckboxDetailStyle(
                            driverEndorsementPublicID,
                            checked
                        ),
                    })}
                >
                    <CheckboxField
                        id={`${id}_${driver.publicID}_checkbox`}
                        className={cx('wmicMasterInputCheckbox', 'gw-pl-5')}
                        labelClassName={cx('wmicMasterInputFieldLabel')}
                        label={getEndorsementLabel(driverEndorsementPublicID)}
                        showInlineLabel
                        onValueChange={(value, path) => handleChange(value, path, driver, setChecked, setIsLoading)}
                        value={checked}
                        path={`${driverEndorsementPublicID}`}
                        readOnly={readOnly}
                    />

                    <WMICDriverEndorsementsDetailsComponent
                        checked={checked}
                        driverEndorsementPublicID={driverEndorsementPublicID}
                        existingEndorsements={WMICDriverEndorsementUtil.getExistingEndorsements(
                            driverEndorsement,
                            driver?.publicID,
                            paLobDataVM
                        )}
                        readOnly={readOnly}
                        vehicles={paLobDataVM?.coverables?.vehicles?.value}
                    />
                </div>
            )}
        </React.Fragment>
    );
}

WMICDriverEndorsementsCheckbox.propTypes = {
    id: PropTypes.string.isRequired,
    driverEndorsement: PropTypes.shape({}).isRequired,
    driver: PropTypes.shape({}).isRequired,
    selectedVehicleId: PropTypes.string,
    baseState: PropTypes.string.isRequired,
    rateAsOfDate: PropTypes.shape({}).isRequired,
    paLobDataVM: PropTypes.shape({}).isRequired,
    handleChange: PropTypes.func.isRequired,
};

export default WMICDriverEndorsementsCheckbox;
